import type { Point } from '../types/document'

export interface PxPoint {
  x: number
  y: number
}

export interface BulbSample {
  x: number
  y: number
  /** 0..1 along the run. */
  t: number
  /** Perspective scale at this point. Drives both size and local spacing. */
  scale: number
  /** Tangent angle, radians. Used to sit the bulb on the wire. */
  angle: number
}

export const lerp = (a: number, b: number, t: number) => a + (b - a) * t
export const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

export const toPx = (p: Point, w: number, h: number): PxPoint => ({ x: p.x * w, y: p.y * h })
export const toNorm = (p: PxPoint, w: number, h: number): Point => ({ x: p.x / w, y: p.y / h })

export function segmentLength(a: PxPoint, b: PxPoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y)
}

export function pathLength(pts: PxPoint[]): number {
  let total = 0
  for (let i = 1; i < pts.length; i++) total += segmentLength(pts[i - 1], pts[i])
  return total
}

/**
 * REAL feet, not photographed feet.
 *
 * A photograph foreshortens. Calibrate on a garage door facing the camera and
 * then measure an eave running away down the side of the house, and the pixels
 * report far less than the tape will: the far end of that run is further from
 * the lens, so every foot of it occupies fewer pixels than a foot on the front
 * of the house did.
 *
 * The renderer already knows this - it spaces bulbs by the same depth taper, so
 * a receding run correctly gets its bulbs closer together on screen. The
 * footage did not, which meant the bulb count and the linear feet quietly
 * disagreed with each other, and the feet were the ones on the invoice.
 *
 * Apparent size is inversely proportional to distance, so a stretch of path
 * whose bulbs render at scale d covers 1/d as many real feet as its pixels
 * suggest. With d running linearly from d0 to d1 along the path, that integral
 * has a closed form:
 *
 *     feet = (px / ppf) * ln(d1/d0) / (d1 - d0)
 *
 * which collapses to the plain px/ppf when d0 and d1 are equal, i.e. when the
 * run is parallel to the camera and nothing needs correcting.
 */
export function pathFeet(
  pts: PxPoint[],
  pixelsPerFoot: number,
  depthStart = 1,
  depthEnd = 1,
): number {
  if (!pixelsPerFoot || pixelsPerFoot <= 0) return 0
  const px = pathLength(pts)
  const d0 = Math.max(0.15, depthStart)
  const d1 = Math.max(0.15, depthEnd)
  const flat = px / pixelsPerFoot
  if (Math.abs(d1 - d0) < 1e-4) return flat / d0
  return (flat * Math.log(d1 / d0)) / (d1 - d0)
}

/**
 * FOOTAGE OFF A PHOTOGRAPH, AND WHY IT IS FLAT BY DEFAULT.
 *
 * pathFeet above can correct for a run receding from the camera, and that
 * correction is real: a run that recedes covers more feet than its pixels
 * suggest. The trouble was that it was applied to EVERY confirmed run out of a
 * default - depthEnd 0.82, worth 10.25% - whether or not that run receded, and
 * whether or not anybody had looked at the photograph and decided it did.
 *
 * On the end-to-end test that was 9.5 ft on one house, about $95, on a front
 * eave that is very nearly parallel to the camera. A number that confident,
 * arrived at from a default nobody chose, is worse than a plainer number.
 *
 * So the taper is now something you ask for. Off, this is the tape reading:
 * pixels divided by the scale. On, it is the perspective-corrected reading,
 * and the person turning it on knows what they are claiming.
 *
 * Either way the result is an ESTIMATE from one reference length in one plane,
 * and the quote says so. The exact number is the one typed in by hand.
 */
export function measuredFeet(
  pts: PxPoint[],
  pixelsPerFoot: number,
  strand: { depthStart: number; depthEnd: number },
  takeoff?: { depthCorrection?: boolean },
): number {
  const correct = takeoff?.depthCorrection === true
  return pathFeet(
    pts,
    pixelsPerFoot,
    correct ? strand.depthStart : 1,
    correct ? strand.depthEnd : 1,
  )
}

/**
 * Subdivide the path and pull each segment downward into a catenary.
 * A real strand between two fixed points hangs, it does not run straight.
 * sag 0 leaves the path untouched.
 */
export function applySag(pts: PxPoint[], sag: number, steps = 14): PxPoint[] {
  if (sag <= 0.001 || pts.length < 2) return pts
  const out: PxPoint[] = [pts[0]]
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]
    const b = pts[i]
    const len = segmentLength(a, b)
    const drop = sag * len * 0.28
    for (let s = 1; s <= steps; s++) {
      const u = s / steps
      // sin gives the hanging belly, zero at both anchors
      const dip = Math.sin(Math.PI * u) * drop
      out.push({ x: lerp(a.x, b.x, u), y: lerp(a.y, b.y, u) + dip })
    }
  }
  return out
}

/**
 * Hang the run in repeating scallops instead of one long droop.
 *
 * A railing is not a rope slung between two distant posts: it is garland fixed
 * at every post with a shallow dip between each pair. Applying one catenary
 * across the whole run - which is what sag alone does - gives a forty foot
 * belly sagging onto the deck, which is why swagged garland never looked like
 * garland.
 *
 * count is how many dips, depth is how far each one falls as a fraction of its
 * own span. Both are per run, because a porch rail and a fascia want different
 * numbers and always will.
 */
export function scallop(pts: PxPoint[], count: number, depth: number, steps = 10): PxPoint[] {
  if (count < 1 || depth <= 0.001 || pts.length < 2) return pts
  const acc = cumulative(pts)
  const total = acc[acc.length - 1]
  if (total <= 0) return pts

  const span = total / count
  const out: PxPoint[] = []
  for (let i = 0; i < count; i++) {
    for (let s = 0; s <= steps; s++) {
      // Skip the shared anchor so consecutive dips do not stack a duplicate.
      if (i > 0 && s === 0) continue
      const u = s / steps
      const at = pointAtDistance(pts, acc, span * (i + u))
      // Perpendicular to the run, so a scalloped gable rake dips square to the
      // rake rather than straight down the photo.
      const dip = Math.sin(Math.PI * u) * span * depth * 0.5
      out.push({
        x: at.p.x - Math.sin(at.angle) * dip * -1,
        y: at.p.y + Math.cos(at.angle) * dip,
      })
    }
  }
  return out
}

/** Densify so we can walk the path at arbitrary distances. */
function cumulative(pts: PxPoint[]): number[] {
  const acc = [0]
  for (let i = 1; i < pts.length; i++) acc.push(acc[i - 1] + segmentLength(pts[i - 1], pts[i]))
  return acc
}

function pointAtDistance(pts: PxPoint[], acc: number[], d: number): { p: PxPoint; angle: number } {
  if (d <= 0) {
    const a = pts[0]
    const b = pts[Math.min(1, pts.length - 1)]
    return { p: a, angle: Math.atan2(b.y - a.y, b.x - a.x) }
  }
  const total = acc[acc.length - 1]
  if (d >= total) {
    const a = pts[pts.length - 2] ?? pts[0]
    const b = pts[pts.length - 1]
    return { p: b, angle: Math.atan2(b.y - a.y, b.x - a.x) }
  }
  let lo = 0
  let hi = acc.length - 1
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1
    if (acc[mid] <= d) lo = mid
    else hi = mid
  }
  const a = pts[lo]
  const b = pts[lo + 1]
  const segLen = acc[lo + 1] - acc[lo] || 1
  const u = (d - acc[lo]) / segLen
  return {
    p: { x: lerp(a.x, b.x, u), y: lerp(a.y, b.y, u) },
    angle: Math.atan2(b.y - a.y, b.x - a.x),
  }
}

/**
 * Walk a strand and place a bulb every `baseSpacing` pixels, with the step
 * shrinking as the run recedes from the camera. This variable step is what
 * makes a roofline look like it belongs to the house rather than sitting on
 * top of the photo.
 */
export function sampleStrand(
  pxPoints: PxPoint[],
  baseSpacing: number,
  depthStart: number,
  depthEnd: number,
  sag: number,
): BulbSample[] {
  if (pxPoints.length < 2) {
    if (pxPoints.length === 1) {
      return [{ x: pxPoints[0].x, y: pxPoints[0].y, t: 0, scale: depthStart, angle: 0 }]
    }
    return []
  }

  const path = applySag(pxPoints, sag)
  const acc = cumulative(path)
  const total = acc[acc.length - 1]
  if (total <= 0) return []

  const spacing = Math.max(2, baseSpacing)
  const out: BulbSample[] = []
  let d = 0
  let guard = 0

  while (d <= total && guard < 5000) {
    guard++
    const t = clamp(d / total, 0, 1)
    const scale = Math.max(0.15, lerp(depthStart, depthEnd, t))
    const { p, angle } = pointAtDistance(path, acc, d)
    out.push({ x: p.x, y: p.y, t, scale, angle })
    d += spacing * scale
  }
  return out
}

/** Distance from a point to a polyline, in pixels. Used for click selection. */
export function distanceToPath(pts: PxPoint[], p: PxPoint): number {
  if (pts.length === 0) return Infinity
  if (pts.length === 1) return segmentLength(pts[0], p)
  let best = Infinity
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]
    const b = pts[i]
    const vx = b.x - a.x
    const vy = b.y - a.y
    const len2 = vx * vx + vy * vy
    let t = len2 === 0 ? 0 : ((p.x - a.x) * vx + (p.y - a.y) * vy) / len2
    t = clamp(t, 0, 1)
    const d = Math.hypot(a.x + vx * t - p.x, a.y + vy * t - p.y)
    if (d < best) best = d
  }
  return best
}
